import Loader from "@/components/loader";
import API from "@/services/api";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FaSort, FaSortUp } from "react-icons/fa";
import { Link, useSearchParams } from "react-router-dom";

export default function List() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [searchParams, setSearchParams] = useSearchParams();
  const sort = searchParams.get("sort") || "full_name";
  const order = searchParams.get("order") || "desc";
  const page = parseInt(searchParams.get("page")) || 1;
  const activity_area = searchParams.get("activity_area") || "";

  function setFilter(key, value) {
    const newParams = new URLSearchParams(searchParams);
    value ? newParams.set(key, value) : newParams.delete(key);
    setSearchParams(newParams);
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setFilter("page", 1);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  async function getProfiles() {
    setLoading(true);
    try {
      const filters = { sort, order, page, size: 50, activity_area, search: debouncedSearch };
      const { ok, code, data: responseData } = await API.post("/malt_profile/search", filters);
      if (!ok) throw new Error(code);
      setData(responseData);
    } catch (error) {
      toast.error("Failed to fetch profiles");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    getProfiles();
  }, [page, sort, order, debouncedSearch, activity_area]);

  const getSortIcon = (field) => {
    if (sort.field !== field) return <FaSort className="inline-block ml-1 text-gray-400" />;
    return sort.order === "asc" ? <FaSortUp className="inline-block ml-1 text-gray-400" /> : <FaSort className="inline-block ml-1 text-gray-400" />;
  };

  const activityAreas = [...new Set(data.profiles?.flatMap((profile) => profile.experiences?.map((exp) => exp.activity_area).filter(Boolean) || []))].sort();

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold mb-6">Malt Profiles</h1>

      {/* Search and Filter Section */}
      <div className="mb-6 bg-white p-4 rounded-md shadow">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <input
              type="text"
              placeholder="Search by name or username..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Activity Area</label>
            <select
              value={activity_area}
              onChange={(e) => setFilter("activity_area", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">All Activity Areas</option>
              {activityAreas.map((area) => (
                <option key={area} value={area}>
                  {area}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-4 text-sm text-gray-600">
          Showing {data?.profiles?.length} of {data?.total} profiles
        </div>
      </div>

      <div className="my-8 rounded-md shadow bg-white">
        {loading ? (
          <Loader className="p-8" />
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => setFilter("sort", "full_name")}>
                  Full Name {getSortIcon("full_name")}
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Experience</th>
                <th
                  className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => setFilter("sort", "scraped_at")}>
                  Scraped At {getSortIcon("scraped_at")}
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Links</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.profiles.map((profile) => (
                <tr key={profile._id}>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <div>
                      <div className="font-medium">{profile.full_name}</div>
                    </div>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {profile.experiences && profile.experiences.length > 0 ? (
                      <div>
                        <div className="font-medium">{profile.experiences[profile.experiences.length - 1].job_title || "No job title"}</div>
                        {profile.experiences[profile.experiences.length - 1].activity_area && (
                          <div className="text-sm text-gray-500">{profile.experiences[profile.experiences.length - 1].activity_area}</div>
                        )}
                      </div>
                    ) : (
                      "No experiences"
                    )}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{profile.scraped_at ? new Date(profile.scraped_at).toLocaleDateString() : "N/A"}</td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <Link to={`/malt/${profile._id}`} className="text-blue-600 hover:underline">
                      Accounting
                    </Link>
                    {" | "}
                    <a href={`https://www.malt.com/profile/${profile.username}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      Malt
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="flex justify-between items-center mt-4">
        <button onClick={() => setFilter("page", Math.max(prev - 1, 1))} disabled={page <= 1} className="px-4 py-2 bg-blue-500 text-white rounded disabled:opacity-50">
          Previous
        </button>
        <span>
          Page {page} of {Math.ceil(data.total / 50)}
        </span>
        <button onClick={() => setFilter("page", page + 1)} className="px-4 py-2 bg-blue-500 text-white rounded disabled:opacity-50">
          Next
        </button>
      </div>
    </div>
  );
}
